import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  InputBase,
  Button,
  Typography,
  Divider,
  createTheme,
  ThemeProvider,
  alpha,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import refresh from "../../assets/img/refresh_icon.svg";
import useSignalingStore from "views/signalingStore";
import colors from "colors/color";

import message_icon from "../../assets/img/message_icon.svg";
import profile_icon from "../../assets/img/profile_icon.svg";
import logout_icon from "../../assets/img/logout_icon.svg";

// Create a custom theme to match the design
const theme = createTheme({
  palette: {
    background: {
      default: "#ffffff",
    },
    text: {
      primary: "#000000",
      secondary: "rgba(0, 0, 0, 0.6)",
    },
    divider: "#d9d9d9",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "9999px",
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});
const MenuTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
  letterSpacing: "0%",
  color: colors.primary,
}));

// Custom styled components with responsive design
const SearchInput = styled(InputBase)(({ theme }) => ({
  paddingLeft: "40px",
  height: 40,
  borderRadius: "10px",
  borderWidth: "1px",
  paddingTop: "11px",
  paddingRight: "20px",
  paddingBottom: "11px",
  gap: "10px",
  bgcolor: "#fff",
  border: "1px solid #D9D9D9",
  width: "100%",
  maxWidth: "411px",

  "&:focus-within": {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.divider, 0.5)}`,
  },
}));

const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  minWidth: "44px",
  minHeight: "44px",
  maxWidth: "44px",
  maxHeight: "44px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  borderRadius: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
  overflow: "hidden",
}));

const SearchIconWrapper = styled("div")({
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(0, 0, 0, 0.4)",
});

const RefreshButton = styled(Button)(({ theme }) => ({
  border: "1px solid #D9D9D9",
  height: 38,
  borderRadius: "10px",
  paddingTop: "10px",
  paddingRight: "20px",
  paddingBottom: "10px",
  paddingLeft: "20px",
  gap: "10px",
  minWidth: "auto",
  whiteSpace: "nowrap",

  "&:hover": {
    background: "rgba(0, 0, 0, 0.04)",
  },

  [theme.breakpoints.down("sm")]: {
    paddingRight: "10px",
    paddingLeft: "10px",
  },
}));

export default function Navbar({ formData, title, onMenuClick }) {
  const {
    signalingServer,
    signalingServer2,
    addMessage,
    addNotification,
    notifications,
    notificationMessages,
    incrementNotification,
    clearNotifications,
    fetchUnreadNotifications: fetchUnreadNotificationsStore,
    markNotificationsAsRead,
    markNotificationAsRead,
    unreadNotifications: storeUnreadNotifications,
    notificationCounts,
    getTotalNotifications,
    messagenotification,
    clearMessageNotifications,
    removeMessageNotification,
    updateNotificationCount,
    setInitialNotificationCounts,
    addMessageNotification,
  } = useSignalingStore();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    console.log("Current title:", title);
  }, [title]);

  useEffect(() => {
    console.log("Current formData:", formData);
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  const handleMenuItemClick = (action) => {
    console.log("Action:", action);

    // Check if user is in admin space
    const isAdminSpace = window.location.pathname.includes("/manager");

    if (action === "messages") {
      if (isAdminSpace) {
        navigate("/manager/Chat");
      } else {
        navigate("/talent/chat");
      }
    }
    if (action === "profile") {
      if (isAdminSpace) {
        navigate("/manager/AdminProfile");
      } else {
        navigate("/profile");
      }
    }
    if (action === "talent_profile") {
      if (isAdminSpace) {
        navigate("/manager/AdminProfile");
      } else {
        navigate("/talent/user-page");
      }
    }
    if (action === "saved") {
      navigate("/saved_posts");
    }
    if (action === "applied") {
      navigate("/post_applied");
    }
    if (action === "logout") {
      localStorage.clear();
      // This will do both navigation and hard refresh
      window.location.href = "/landing";
    }
    handleClose();
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  // Check if user is in admin space for context-aware labels
  const isAdminSpace = window.location.pathname.includes("/manager");

  // Reusable notification badge component
  const NotificationBadge = ({ count, color = "#0017E7" }) => (
    <Box
      sx={{
        ml: "auto",
        backgroundColor: color,
        color: "white",
        borderRadius: "50%",
        width: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: "bold",
      }}
    >
      {count}
    </Box>
  );
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: "100%",
          bgcolor: "background.default",
          border: "1px solid #D9D9D9",
          borderLeft: "none",
          margin: 0,
          padding: 0,
          "@media (max-width: 768px)": {
            borderLeft: "none",
            borderRight: "none",
            borderTop: "none",
          },
        }}
      >
        <Container
          sx={{
            px: { xs: 2, md: 4 },
            maxWidth: "100%",
            py: 0,
          }}
        >
          <Box
            sx={{
              py: { xs: 0.5, md: 1 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
                mt: { xs: 1, md: 0 },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  // justifyContent: "center",
                  height: "auto",
                  minHeight: { xs: 40, md: 50 },
                  // borderTop: '1px solid #D9D9D9',
                  // padding: { xs: '10px 15px', md: '14px 25px' },
                  gap: "10px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: "14px",
                    
                    color: "#1a237e",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: { xs: "normal", md: "nowrap" },
                    ml : {xs: 4 , md: 0},
                    display: "flex",
                    alignItems: "center",
                    height: "100%"
                  }}
                >
                  {title}
                </Typography>
              </Box>

              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  ml: 1,
                  display: { xs: "flex", md: "none" },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: { xs: 1.5, md: 3 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "space-between", md: "flex-end" },
              }}
            >

              <Divider
                orientation="vertical"
                sx={{
                  height: "54px",
                  borderColor: "#D9D9D9",
                  display: { xs: "none", sm: "block" },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                }}
                onClick={handleAvatarClick}
              >
                <CompanyLogo>
                  <img
                    src={formData?.created_by?.company_logo}
                    alt="Company Logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </CompanyLogo>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "14px",
                      color: "#000000",
                    }}
                  >
                    {formData?.first_name} 
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      fontSize: "9px",
                    }}
                  >
                    {isAdminSpace
                      ? t("navbar.administrator") || "Administrator"
                      : t("navbar.recruiter")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            bgcolor: "background.default",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #D9D9D9",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
              }}
              onClick={handleAvatarClick}
            >
              <CompanyLogo>
                <img
                  src={formData?.created_by?.company_logo}
                  alt="Company Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </CompanyLogo>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#000000",
                  }}
                >
                  {formData?.company}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: "9px",
                  }}
                >
                  {isAdminSpace ? "Administrator" : "Recruiter"}
                </Typography>
              </Box>
            </Box>
          </Box>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 0 }}>
          <ListItem button>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img
                    src={refresh}
                    alt="Refresh"
                    style={{ width: 16, height: 16 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Refresh data
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary={
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  {title}
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Drawer>

      <Menu
        id="avatar-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "avatar-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 167,
            borderRadius: "10px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiList-root": {
              py: 1,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuItemClick("messages");
          }}
          sx={{
            py: 2,
            "&:hover": { backgroundColor: "rgba(0, 23, 231, 0.04)" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                mr: 1.5,
                display: "flex",
                justifyContent: "center",
                color: "#0017E7",
              }}
            >
              <img src={message_icon} />
            </Box>
            <MenuTypography>{t("navBar.messages")}</MenuTypography>
            {messagenotification.length > 0 && (
              <NotificationBadge count={messagenotification.length} />
            )}
          </Box>
        </MenuItem>
        <Divider sx={{ borderTop: "1px solid #eee", mx: 2 }} />

        <MenuItem
          onClick={() => handleMenuItemClick("talent_profile")}
          sx={{
            py: 2,
            "&:hover": { backgroundColor: "rgba(0, 23, 231, 0.04)" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                mr: 1.5,
                display: "flex",
                justifyContent: "center",
                color: "#0017E7",
              }}
            >
              <img src={profile_icon} />
            </Box>
            <MenuTypography>
              {isAdminSpace
                ? t("navbar.admin_profile2") || "Admin Profile"
                : t("navbar.profile")}
            </MenuTypography>
          </Box>
        </MenuItem>
        <Divider sx={{ borderTop: "1px solid #eee", mx: 2, display: "none" }} />

        <Divider sx={{ borderTop: "1px solid #eee", mx: 2 }} />
        <Box>
          <MenuItem
            onClick={() => handleMenuItemClick("logout")}
            sx={{
              py: 2,
              "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.04)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  mr: 1.5,
                  display: "flex",
                  justifyContent: "center",
                  color: "#FF0000",
                }}
              >
                <img src={logout_icon} />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#FF0000",
                }}
              >
                {t("navBar.log_out")}
              </Typography>
            </Box>
          </MenuItem>
        </Box>
      </Menu>
    </ThemeProvider>
  );
}
